/**
 * useExportCouleurPDF.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Hook d'export PDF pour CouleurView – Nawara BI
 *
 * Génère un PDF complet avec :
 *  • Page de couverture
 *  • Page Tableau  (mode tableau)
 *  • Page Dashboard (résumé des données)
 *  • Page Comparaison (info + données si disponibles)
 *  • Page Évolution (info + données si disponibles)
 *
 * Dépendances : jspdf  (npm install jspdf)
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback } from "react";
import jsPDF from "jspdf";
import { colors } from "../../../shared/ui/tokens";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CouleurPDFRow {
  couleur: string;
  values: number[];
  coutTTC?: number | null;
  marge?: number | null;
}

export interface CouleurExportPDFOptions {
  filename?: string;
  dateFrom?: string;
  dateTo?: string;
  topN?: number | null;
  headers: string[];
  rows: CouleurPDFRow[];
  grandTotals: number[];
  totalCoutTTC?: number | null;
  totalMarge?: number | null;
  hasCoutTTC?: boolean;
  hasMarge?: boolean;
  selectedMagasin?: string | null;
  soldeCode?: string | null;
  demarquePhase?: string | null;
  viewMode: "tableau" | "dashboard" | "comparaison" | "evolution";
  // pour dashboard : données brutes
  dashboardColumns?: string[];
  dashboardRows?: any[][];
}

// ── Dimensions PDF ─────────────────────────────────────────────────────────────

const PW = 297; // A4 paysage largeur mm
const PH = 210; // A4 paysage hauteur mm
const ML = 12;
const MR = 12;
const MT = 12;
const CW = PW - ML - MR;

// ── Palette ────────────────────────────────────────────────────────────────────

const C = {
  violet: colors.brand[700],
  violetDark: colors.brand[700],
  violetLight: colors.neutral[100],
  violetMed: colors.brand[100],
  rose: colors.brand[300],
  teal: colors.accent[500],
  success: colors.accent[500],
  danger: colors.danger[500],
  gray50: colors.neutral[50],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  white: colors.neutral[0],
};

// ── Filtre valeurs indésirables ──────────────────────────────────────────────
const INVALID_DIM_RE = /^\s*(all|unknown|couleur inconnue|inconnue|non renseign[ée]|n\/a|na|null|undefined|#n\/a)\s*$/i;
/** Retourne false si la valeur est vide, "all", "Unknown", "inconnue", etc. */
function isValidDimension(v: string | null | undefined): boolean {
  if (!v || v.trim() === "") return false;
  return !INVALID_DIM_RE.test(v.trim());
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function lightenRgb(hex: string, amount: number): [number, number, number] {
  const [r, g, b] = hexToRgb(hex);
  return [
    Math.round(r + (255 - r) * amount),
    Math.round(g + (255 - g) * amount),
    Math.round(b + (255 - b) * amount),
  ];
}

function setFill(doc: jsPDF, hex: string) {
  const [r, g, b] = hexToRgb(hex);
  doc.setFillColor(r, g, b);
}

function setDraw(doc: jsPDF, hex: string) {
  const [r, g, b] = hexToRgb(hex);
  doc.setDrawColor(r, g, b);
}

function setTextColor(doc: jsPDF, hex: string) {
  const [r, g, b] = hexToRgb(hex);
  doc.setTextColor(r, g, b);
}

function fmt(n: number | null | undefined): string {
  if (n == null || isNaN(n as number)) return "—";
  // Remplace l'espace fine insécable (U+202F) généré par fr-FR par un espace normal
  // car jsPDF / Helvetica ne supporte pas U+202F et le rend comme "/" en décalant les largeurs
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 })
    .format(n as number)
    .replace(/\u202F/g, " ");
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

// ── En-tête de page standard ───────────────────────────────────────────────────

function drawPageHeader(
  doc: jsPDF,
  title: string,
  subtitle: string,
  pageNum: number,
  totalPages: number,
) {
  // Bande violette
  setFill(doc, C.violetDark);
  doc.rect(0, 0, PW, 20, "F");

  // Ligne rose accent
  setFill(doc, C.rose);
  doc.rect(0, 18, PW, 2, "F");

  // Titre
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  setTextColor(doc, C.white);
  doc.text(`🎨  ${title}`, ML, 13);

  // Pagination
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  setTextColor(doc, C.violetMed);
  doc.text(`Page ${pageNum} / ${totalPages}`, PW - MR, 13, { align: "right" });

  // Sous-bande légère
  const [lr, lg, lb] = lightenRgb(C.violet, 0.92);
  doc.setFillColor(lr, lg, lb);
  doc.rect(0, 20, PW, 7, "F");

  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  setTextColor(doc, C.violet);
  doc.text(subtitle, ML, 25.5);
}

// ── Footer ─────────────────────────────────────────────────────────────────────

function drawFooter(doc: jsPDF, pageNum: number, totalPages: number) {
  const [lr, lg, lb] = lightenRgb(C.violet, 0.6);
  doc.setDrawColor(lr, lg, lb);
  doc.setLineWidth(0.3);
  doc.line(ML, PH - 8, PW - MR, PH - 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  const [gr, gg, gb] = hexToRgb(C.gray500);
  doc.setTextColor(gr, gg, gb);
  doc.text(
    `AvDash · Analyse Couleur · ${new Date().toLocaleDateString("fr-FR")}`,
    ML,
    PH - 4,
  );
  doc.text(`${pageNum} / ${totalPages}`, PW - MR, PH - 4, { align: "right" });
}

// ── Page de couverture ─────────────────────────────────────────────────────────

function drawCoverPage(
  doc: jsPDF,
  opts: CouleurExportPDFOptions,
  totalPages: number,
) {
  // Fond dégradé simulé
  const [lr, lg, lb] = lightenRgb(C.violet, 0.96);
  doc.setFillColor(lr, lg, lb);
  doc.rect(0, 0, PW, PH, "F");

  // Bande supérieure
  setFill(doc, C.violetDark);
  doc.rect(0, 0, PW, 52, "F");

  // Accent rose
  setFill(doc, C.rose);
  doc.rect(0, 50, PW, 3, "F");

  // Titre principal
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  setTextColor(doc, C.white);
  doc.text("Analyse Couleur", ML, 24);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  setTextColor(doc, "#D7E5F5");
  doc.text("AvDash  —  Rapport d'export", ML, 36);

  // Sous-titre période
  doc.setFontSize(10);
  setTextColor(doc, "#B0CBEB");
  const paramParts: string[] = [];
  if (opts.soldeCode) paramParts.push(`Solde : ${opts.soldeCode}`);
  if (opts.demarquePhase) paramParts.push(`Démarque : ${opts.demarquePhase}`);
  if (opts.dateFrom && opts.dateTo)
    paramParts.push(`${opts.dateFrom} → ${opts.dateTo}`);
  if (opts.selectedMagasin)
    paramParts.push(`Magasin : ${opts.selectedMagasin}`);
  if (opts.topN) paramParts.push(`Top ${opts.topN}`);
  if (paramParts.length > 0) doc.text(paramParts.join("   |   "), ML, 46);

  // Date de génération
  doc.setFontSize(8);
  doc.text(`Généré le : ${new Date().toLocaleString("fr-FR")}`, PW - MR, 46, {
    align: "right",
  });

  // Carte résumé
  const cardY = 62;
  const cardH = 80;
  setFill(doc, C.white);
  doc.roundedRect(ML, cardY, CW, cardH, 4, 4, "F");
  setDraw(doc, C.violetMed);
  doc.setLineWidth(0.5);
  doc.roundedRect(ML, cardY, CW, cardH, 4, 4, "D");

  // Accent gauche carte
  setFill(doc, C.violet);
  doc.rect(ML, cardY, 4, cardH, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  setTextColor(doc, C.violet);
  doc.text("Résumé des données", ML + 9, cardY + 12);

  // Stats
  const statsX = ML + 9;
  let statsY = cardY + 22;
  const lineH = 12;

  const addStat = (label: string, value: string) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    setTextColor(doc, C.gray500);
    doc.text(label, statsX, statsY);
    doc.setFont("helvetica", "normal");
    setTextColor(doc, C.gray800);
    doc.text(value, statsX + 48, statsY);
    statsY += lineH;
  };

  addStat("Mesures :", opts.headers.join(", "));
  addStat(
    "Couleurs trouvées :",
    `${opts.rows.length}${opts.topN ? ` (Top ${opts.topN})` : ""}`,
  );
  addStat(
    "Vue exportée :",
    opts.viewMode.charAt(0).toUpperCase() + opts.viewMode.slice(1),
  );
  if (opts.rows.length > 0 && opts.grandTotals.length > 0) {
    addStat("Total " + (opts.headers[0] ?? ""), fmt(opts.grandTotals[0]));
  }
  addStat("Pages du rapport :", `${totalPages} pages`);

  // Liste des vues incluses
  const viewsY = cardY + cardH + 16;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  setTextColor(doc, C.violet);
  doc.text("Vues incluses dans ce rapport :", ML, viewsY);

  const views = [
    { label: "Tableau", icon: "📋", color: C.violet },
    { label: "Dashboard", icon: "📊", color: "#1D5BA8" },
    { label: "Comparaison", icon: "⚖️", color: "#1D5BA8" },
    { label: "Évolution", icon: "📈", color: C.success },
  ];

  views.forEach((v, i) => {
    const vx = ML + i * (CW / 4);
    const vy = viewsY + 8;
    const [vlr, vlg, vlb] = lightenRgb(v.color, 0.88);
    doc.setFillColor(vlr, vlg, vlb);
    doc.roundedRect(vx, vy, CW / 4 - 4, 18, 3, 3, "F");
    setFill(doc, v.color);
    doc.rect(vx, vy, 3, 18, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    setTextColor(doc, v.color);
    doc.text(`${v.icon}  ${v.label}`, vx + 7, vy + 11);
  });

  // Footer couverture
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  setTextColor(doc, C.gray500);
  doc.text("AvDash – Rapport Analyse Couleur", ML, PH - 6);
  doc.text("Page 1", PW - MR, PH - 6, { align: "right" });
}

// ── Page Tableau ───────────────────────────────────────────────────────────────

function drawTableauPage(
  doc: jsPDF,
  opts: CouleurExportPDFOptions,
  pageNum: number,
  totalPages: number,
) {
  const {
    headers,
    rows,
    grandTotals,
    totalCoutTTC,
    totalMarge,
    hasCoutTTC,
    hasMarge,
    dateFrom,
    dateTo,
    topN,
    selectedMagasin,
    soldeCode,
    demarquePhase,
  } = opts;

  const paramParts: string[] = [];
  if (soldeCode) paramParts.push(`Solde: ${soldeCode}`);
  if (demarquePhase) paramParts.push(`Démarque: ${demarquePhase}`);
  if (dateFrom && dateTo) paramParts.push(`${dateFrom} → ${dateTo}`);
  if (selectedMagasin) paramParts.push(`Magasin: ${selectedMagasin}`);
  if (topN) paramParts.push(`Top ${topN}`);

  drawPageHeader(
    doc,
    "Analyse Couleur  —  Tableau",
    paramParts.join("   |   ") || "Toutes dates",
    pageNum,
    totalPages,
  );

  if (rows.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(12);
    setTextColor(doc, C.gray500);
    doc.text(
      "Aucune donnée disponible. Lancez une analyse d'abord.",
      PW / 2,
      PH / 2,
      { align: "center" },
    );
    drawFooter(doc, pageNum, totalPages);
    return;
  }

  const startY = 30;

  // Construire les colonnes
  const allCols = ["Couleur", ...headers];
  if (hasCoutTTC) allCols.push("Coût TTC");
  if (hasMarge) allCols.push("Marge");

  const maxRows = Math.min(rows.length, 35);
  const colW0 = 38; // largeur colonne Couleur
  const remainW = CW - colW0;
  const colWData = remainW / (allCols.length - 1);

  const rowH = 5.5;
  const hY = startY;

  // En-tête tableau
  allCols.forEach((h, i) => {
    const x = ML + (i === 0 ? 0 : colW0 + (i - 1) * colWData);
    const w = i === 0 ? colW0 : colWData;
    const bg =
      h === "Coût TTC"
        ? C.teal
        : h === "Marge"
          ? C.success
          : i === 0
            ? C.violetDark
            : C.violet;
    setFill(doc, bg);
    doc.rect(x, hY, w, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    setTextColor(doc, C.white);
    doc.text(
      truncate(h, i === 0 ? 22 : 14),
      x + (i === 0 ? 2 : w / 2),
      hY + 4.8,
      { align: i === 0 ? "left" : "center" },
    );
  });

  // Lignes de données
  rows.slice(0, maxRows).forEach((row, idx) => {
    const y = hY + 7 + idx * rowH;

    // Fond alterné
    if (idx % 2 === 1) {
      const [lr, lg, lb] = lightenRgb(C.violet, 0.95);
      doc.setFillColor(lr, lg, lb);
      doc.rect(ML, y, CW, rowH, "F");
    }

    // Bordure ligne
    setDraw(doc, C.gray200);
    doc.setLineWidth(0.1);
    doc.line(ML, y + rowH, ML + CW, y + rowH);

    // Nom couleur
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    setTextColor(doc, C.gray800);
    doc.text(truncate(row.couleur, 24), ML + 2, y + rowH - 1.5);

    // Valeurs
    row.values.forEach((val, vi) => {
      const x = ML + colW0 + vi * colWData;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      setTextColor(doc, C.gray800);
      const rx = Math.min(x + colWData - 2, ML + CW - 2);
      doc.text(fmt(val), rx, y + rowH - 1.5, { align: "right" });
    });

    // Coût TTC
    if (hasCoutTTC && row.coutTTC != null) {
      const x = ML + colW0 + headers.length * colWData;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      setTextColor(doc, C.teal);
      const rx = Math.min(x + colWData - 2, ML + CW - 2);
      doc.text(fmt(row.coutTTC), rx, y + rowH - 1.5, { align: "right" });
    }

    // Marge
    if (hasMarge && row.marge != null) {
      const x = ML + colW0 + (headers.length + (hasCoutTTC ? 1 : 0)) * colWData;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      setTextColor(doc, row.marge >= 0 ? C.success : C.danger);
      const rx = Math.min(x + colWData - 2, ML + CW - 2);
      doc.text(
        (row.marge >= 0 ? "+" : "−") + fmt(Math.abs(row.marge)),
        rx,
        y + rowH - 1.5,
        { align: "right" },
      );
    }
  });

  if (rows.length > maxRows) {
    const truncY = hY + 7 + maxRows * rowH + 4;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    setTextColor(doc, C.gray500);
    doc.text(
      `… et ${rows.length - maxRows} autres couleurs (export Excel pour voir tout)`,
      ML,
      truncY,
    );
  }

  // Ligne Total
  if (rows.length > 1) {
    const totY =
      hY +
      7 +
      Math.min(rows.length, maxRows) * rowH +
      (rows.length > maxRows ? 8 : 3);
    setFill(doc, C.violetDark);
    doc.rect(ML, totY, CW, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    setTextColor(doc, C.white);
    doc.text(`∑ TOTAL  (${rows.length} couleurs)`, ML + 2, totY + 4.8);
    grandTotals.forEach((val, i) => {
      const x = ML + colW0 + i * colWData;
      setTextColor(doc, "#D7E5F5");
      const rx = Math.min(x + colWData - 2, ML + CW - 2);
      doc.text(fmt(val), rx, totY + 4.8, { align: "right" });
    });
    if (hasCoutTTC && totalCoutTTC != null) {
      const x = ML + colW0 + headers.length * colWData;
      setTextColor(doc, C.white);
      const rx = Math.min(x + colWData - 2, ML + CW - 2);
      doc.text(fmt(totalCoutTTC), rx, totY + 4.8, { align: "right" });
    }
    if (hasMarge && totalMarge != null) {
      const x = ML + colW0 + (headers.length + (hasCoutTTC ? 1 : 0)) * colWData;
      setTextColor(doc, totalMarge >= 0 ? "#D3EFEA" : "#F9D4D4");
      const rx = Math.min(x + colWData - 2, ML + CW - 2);
      doc.text(
        (totalMarge >= 0 ? "+" : "−") + fmt(Math.abs(totalMarge)),
        rx,
        totY + 4.8,
        { align: "right" },
      );
    }
  }

  drawFooter(doc, pageNum, totalPages);
}

// ── Page Dashboard ────────────────────────────────────────────────────────────

function drawDashboardPage(
  doc: jsPDF,
  opts: CouleurExportPDFOptions,
  pageNum: number,
  totalPages: number,
) {
  const {
    headers,
    rows,
    grandTotals,
    dateFrom,
    dateTo,
    topN,
    selectedMagasin,
    soldeCode,
  } = opts;

  drawPageHeader(
    doc,
    "Analyse Couleur  —  Dashboard",
    [
      soldeCode && `Solde: ${soldeCode}`,
      dateFrom && dateTo && `${dateFrom} → ${dateTo}`,
      selectedMagasin && `Magasin: ${selectedMagasin}`,
      topN && `Top ${topN}`,
    ]
      .filter(Boolean)
      .join("   |   ") || "Vue Dashboard",
    pageNum,
    totalPages,
  );

  if (rows.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(12);
    setTextColor(doc, C.gray500);
    doc.text("Aucune donnée disponible.", PW / 2, PH / 2, { align: "center" });
    drawFooter(doc, pageNum, totalPages);
    return;
  }

  const topRows = rows.slice(0, Math.min(rows.length, 15));
  const mainMeasureIdx = 0;
  const totalMain = grandTotals[mainMeasureIdx] ?? 1;
  const maxVal = Math.max(...topRows.map((r) => r.values[mainMeasureIdx] ?? 0));

  const barAreaX = ML;
  const barAreaY = 32;
  const barAreaW = CW * 0.65;
  const barH = (PH - barAreaY - 20) / topRows.length - 1;

  // Titre mesure principale
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  setTextColor(doc, C.violet);
  doc.text(
    `📊  Top ${topRows.length} Couleurs  —  ${headers[mainMeasureIdx] ?? "Valeur"}`,
    ML,
    barAreaY - 4,
  );

  topRows.forEach((row, idx) => {
    const val = row.values[mainMeasureIdx] ?? 0;
    const pct = totalMain > 0 ? val / totalMain : 0;
    const barW = maxVal > 0 ? (val / maxVal) * barAreaW * 0.78 : 0;
    const y = barAreaY + idx * (barH + 1);

    // Fond ligne
    if (idx % 2 === 0) {
      const [lr, lg, lb] = lightenRgb(C.violet, 0.96);
      doc.setFillColor(lr, lg, lb);
      doc.rect(barAreaX, y, CW, barH, "F");
    }

    // Rang
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    setTextColor(doc, C.violet);
    doc.text(`${idx + 1}`, barAreaX + 3, y + barH * 0.68);

    // Nom couleur
    setTextColor(doc, C.gray800);
    doc.setFont("helvetica", "normal");
    doc.text(truncate(row.couleur, 14), barAreaX + 9, y + barH * 0.68);

    // Barre
    const barStartX = barAreaX + 44;
    const [lr, lg, lb] = lightenRgb(C.violet, 0.7);
    doc.setFillColor(lr, lg, lb);
    doc.roundedRect(barStartX, y + 0.8, barAreaW * 0.78, barH - 1.6, 1, 1, "F");
    setFill(doc, C.violet);
    if (barW > 0)
      doc.roundedRect(barStartX, y + 0.8, barW, barH - 1.6, 1, 1, "F");

    // Valeur + %
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    setTextColor(doc, C.gray700);
    doc.text(fmt(val), barStartX + barAreaW * 0.78 + 2, y + barH * 0.72);
    doc.setFont("helvetica", "normal");
    setTextColor(doc, C.gray500);
    doc.text(
      `${(pct * 100).toFixed(1)}%`,
      barStartX + barAreaW * 0.78 + 18,
      y + barH * 0.72,
    );
  });

  // Panneau résumé à droite
  const rX = ML + CW * 0.68;
  const rW = CW * 0.32;
  const rY = barAreaY - 4;

  setFill(doc, C.violetLight);
  doc.roundedRect(rX, rY, rW, 90, 4, 4, "F");
  setFill(doc, C.violet);
  doc.rect(rX, rY, 3, 90, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  setTextColor(doc, C.violet);
  doc.text("Résumé", rX + 6, rY + 10);

  let ry = rY + 18;
  const addSummaryLine = (label: string, value: string, color = C.gray800) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    setTextColor(doc, C.gray500);
    doc.text(label, rX + 6, ry);
    doc.setFont("helvetica", "bold");
    setTextColor(doc, color);
    doc.text(value, rX + rW - 4, ry, { align: "right" });
    ry += 11;
  };

  headers.forEach((h, i) => {
    addSummaryLine(h, fmt(grandTotals[i] ?? 0), C.violet);
  });
  addSummaryLine("Nb couleurs", `${rows.length}`, C.gray700);
  if (opts.hasMarge && opts.totalMarge != null) {
    addSummaryLine(
      "Marge totale",
      fmt(opts.totalMarge),
      opts.totalMarge >= 0 ? C.success : C.danger,
    );
  }

  drawFooter(doc, pageNum, totalPages);
}

// ── Page Comparaison ──────────────────────────────────────────────────────────

function drawComparaisonPage(
  doc: jsPDF,
  opts: CouleurExportPDFOptions,
  pageNum: number,
  totalPages: number,
) {
  drawPageHeader(
    doc,
    "Analyse Couleur  —  Comparaison",
    "Vue Comparaison — Lancez une comparaison dans l'interface pour visualiser les périodes",
    pageNum,
    totalPages,
  );

  // Info card
  const cardY = 34;
  const [lr, lg, lb] = lightenRgb("#1D5BA8", 0.94);
  doc.setFillColor(lr, lg, lb);
  doc.roundedRect(ML, cardY, CW, 60, 4, 4, "F");
  setFill(doc, "#1D5BA8");
  doc.rect(ML, cardY, 4, 60, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  setTextColor(doc, "#1D5BA8");
  doc.text("⚖️  Mode Comparaison de Périodes", ML + 9, cardY + 14);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setTextColor(doc, C.gray700);
  const lines = [
    "Ce mode permet de comparer les performances par couleur sur plusieurs périodes simultanément.",
    "Pour obtenir les données de comparaison dans ce PDF :",
    "  1. Sélectionnez le mode « Comparaison » dans l'interface",
    "  2. Configurez vos périodes à comparer",
    "  3. Cliquez sur Calculer, puis exportez à nouveau en PDF",
  ];
  lines.forEach((line, i) => {
    doc.text(line, ML + 9, cardY + 26 + i * 8);
  });

  // Données du tableau si disponibles
  if (opts.rows.length > 0) {
    const dataY = cardY + 72;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    setTextColor(doc, C.violet);
    doc.text("📋  Données actuelles (vue Tableau)", ML, dataY);

    const topRows = opts.rows.slice(0, 20);
    const rowH = 5.5;
    const colW = CW / (opts.headers.length + 1);

    // Headers
    ["Couleur", ...opts.headers].forEach((h, i) => {
      const x = ML + i * (i === 0 ? colW * 1.5 : colW * 0.9);
      setFill(doc, i === 0 ? C.violetDark : C.violet);
      const w = i === 0 ? colW * 1.5 : colW * 0.9;
      doc.rect(x, dataY + 6, w, 6, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      setTextColor(doc, C.white);
      doc.text(truncate(h, 12), x + 2, dataY + 10.5);
    });

    topRows.forEach((row, idx) => {
      const y = dataY + 12 + idx * rowH;
      if (idx % 2 === 1) {
        const [flr, flg, flb] = lightenRgb(C.violet, 0.95);
        doc.setFillColor(flr, flg, flb);
        doc.rect(ML, y, CW, rowH, "F");
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      setTextColor(doc, C.gray800);
      doc.text(truncate(row.couleur, 18), ML + 2, y + rowH - 1.5);
      row.values.forEach((val, vi) => {
        const x = ML + colW * 1.5 + vi * colW * 0.9;
        doc.text(fmt(val), x + colW * 0.9 - 2, y + rowH - 1.5, {
          align: "right",
        });
      });
    });
  }

  drawFooter(doc, pageNum, totalPages);
}

// ── Page Évolution ─────────────────────────────────────────────────────────────

function drawEvolutionPage(
  doc: jsPDF,
  opts: CouleurExportPDFOptions,
  pageNum: number,
  totalPages: number,
) {
  drawPageHeader(
    doc,
    "Analyse Couleur  —  Évolution",
    "Vue Évolution — Tendances temporelles par couleur",
    pageNum,
    totalPages,
  );

  // Info card
  const cardY = 34;
  const [lr, lg, lb] = lightenRgb(C.success, 0.94);
  doc.setFillColor(lr, lg, lb);
  doc.roundedRect(ML, cardY, CW, 55, 4, 4, "F");
  setFill(doc, C.success);
  doc.rect(ML, cardY, 4, 55, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  setTextColor(doc, C.success);
  doc.text("📈  Mode Évolution Temporelle", ML + 9, cardY + 14);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setTextColor(doc, C.gray700);
  const lines = [
    "Ce mode affiche l'évolution des mesures par couleur sur une période glissante.",
    "Pour obtenir le graphique d'évolution dans ce PDF :",
    "  1. Sélectionnez le mode « Évolution » dans l'interface",
    "  2. Définissez la période et la mesure à suivre",
    "  3. Cliquez sur Calculer, puis exportez à nouveau en PDF",
  ];
  lines.forEach((line, i) => {
    doc.text(line, ML + 9, cardY + 26 + i * 7);
  });

  // Top couleurs visuellement
  if (opts.rows.length > 0) {
    const chartY = cardY + 65;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    setTextColor(doc, C.violet);
    doc.text("📊  Classement actuel par couleur", ML, chartY);

    const topRows = opts.rows.slice(0, 10);
    const maxVal = Math.max(...topRows.map((r) => r.values[0] ?? 0));
    const barAreaH = PH - chartY - 20;
    const barW = (CW - 10) / topRows.length;

    topRows.forEach((row, idx) => {
      const val = row.values[0] ?? 0;
      const barH2 = maxVal > 0 ? (val / maxVal) * (barAreaH - 20) : 0;
      const x = ML + idx * barW;
      const barY = chartY + barAreaH - barH2 - 5;

      // Barre
      const [flr, flg, flb] = lightenRgb(
        C.violet,
        0.3 + (idx / topRows.length) * 0.5,
      );
      doc.setFillColor(flr, flg, flb);
      doc.roundedRect(x + 2, barY, barW - 4, barH2, 2, 2, "F");

      // Label
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.5);
      setTextColor(doc, C.gray800);
      const label = truncate(row.couleur, 6);
      doc.text(label, x + barW / 2, chartY + barAreaH - 1, { align: "center" });

      // Valeur
      doc.setFont("helvetica", "bold");
      doc.setFontSize(5.5);
      setTextColor(doc, C.violet);
      doc.text(fmt(val), x + barW / 2, barY - 1.5, { align: "center" });
    });
  }

  drawFooter(doc, pageNum, totalPages);
}

// ── Hook principal ─────────────────────────────────────────────────────────────

export function useExportCouleurPDF() {
  const exportPDF = useCallback(async (opts: CouleurExportPDFOptions) => {
    const filename =
      opts.filename ??
      `Couleur_Export_${new Date().toISOString().slice(0, 10)}`;

    // Filtrer les valeurs indésirables dans les lignes
    opts = { ...opts, rows: (opts.rows ?? []).filter(r => isValidDimension(r.couleur)) };

    // 1 page couverture + 4 vues
    const totalPages = 5;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    // Page 1 : Couverture
    drawCoverPage(doc, opts, totalPages);

    // Page 2 : Tableau
    doc.addPage();
    drawTableauPage(doc, opts, 2, totalPages);

    // Page 3 : Dashboard
    doc.addPage();
    drawDashboardPage(doc, opts, 3, totalPages);

    // Page 4 : Comparaison
    doc.addPage();
    drawComparaisonPage(doc, opts, 4, totalPages);

    // Page 5 : Évolution
    doc.addPage();
    drawEvolutionPage(doc, opts, 5, totalPages);

    doc.save(`${filename}.pdf`);
  }, []);

  return { exportPDF };
}